// FILE: src/services/foundingTesterService.js
//
// Client helpers for the Founding Tester Recognition Program (migration 050).
//
// Two audiences:
//   • Public / learner — founder_wall() and my_founding_tester() are safe,
//     read-only RPCs. founder_wall() returns only public display fields.
//   • Admin — list / search / upsert / remove. The RPCs re-check admin on the
//     server, so a non-admin call simply errors; nothing here is trusted.
//
// No Redux. Pure async helpers over supabase.rpc.

import { supabase } from './supabaseClient';

// ── Public Founder Wall feed ────────────────────────────────────────────────
/**
 * @returns {Promise<Array<{
 *   display_name:string, certifications:string[], joined_at:string,
 *   contribution_notes:string|null, discord_username:string|null, awarded_at:string
 * }>>}
 */
export async function getFounderWall() {
  try {
    const { data, error } = await supabase.rpc('founder_wall');
    if (error) {
      console.warn('[foundingTesterService] founder_wall failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.warn('[foundingTesterService] founder_wall threw:', e);
    return [];
  }
}

// ── "Am I a Founding Tester?" — module-cached so the badge can mount on
// several surfaces without re-querying. Keyed by nothing (per-session JWT);
// call resetMyFoundingTesterCache() on sign-out / sign-in.
let _myStatusPromise = null;

/**
 * @returns {Promise<null | { awarded_at:string, contribution_notes:string|null }>}
 */
export async function getMyFoundingTester() {
  if (_myStatusPromise) return _myStatusPromise;
  _myStatusPromise = (async () => {
    try {
      const { data, error } = await supabase.rpc('my_founding_tester');
      if (error) {
        console.warn('[foundingTesterService] my_founding_tester failed:', error.message);
        return null;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row || row.is_founding_tester !== true) return null;
      return { awarded_at: row.awarded_at, contribution_notes: row.contribution_notes || null };
    } catch (e) {
      console.warn('[foundingTesterService] my_founding_tester threw:', e);
      return null;
    }
  })();
  return _myStatusPromise;
}

export function resetMyFoundingTesterCache() {
  _myStatusPromise = null;
}

// ── Admin ───────────────────────────────────────────────────────────────────
export async function adminListFoundingTesters() {
  const { data, error } = await supabase.rpc('admin_list_founding_testers');
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

/**
 * Learners who registered via the beta landing page (hold the founding_tester
 * achievement) AND have completed >= 1 exam session. Admin-gated (migration 053).
 * Helps identify who to recognize; `is_founding_tester` flags who is already curated.
 *
 * @returns {Promise<Array<{
 *   user_id:string, email:string, display_name:string|null,
 *   registered_at:string, founding_since:string,
 *   exam_sessions_count:number, last_exam_at:string|null, is_founding_tester:boolean
 * }>>}
 */
export async function adminFoundingCandidates() {
  const { data, error } = await supabase.rpc('admin_founding_candidates');
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function adminSearchUsersForFounding(query) {
  const { data, error } = await supabase.rpc('admin_search_users_for_founding', {
    p_query: query || '',
  });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function adminUpsertFoundingTester({ userId, notes, discord, displayOverride }) {
  const { data, error } = await supabase.rpc('admin_upsert_founding_tester', {
    p_user_id: userId,
    p_notes: notes ?? null,
    p_discord: discord ?? null,
    p_display_override: displayOverride ?? null,
  });
  if (error) throw error;
  resetMyFoundingTesterCache();
  return data; // the row id
}

export async function adminRemoveFoundingTester(userId) {
  const { error } = await supabase.rpc('admin_remove_founding_tester', {
    p_user_id: userId,
  });
  if (error) throw error;
  resetMyFoundingTesterCache();
}
